/**
 * createCrudService Factory
 * 
 * Factory function to create a typed CRUD service for any entity.
 * Eliminates boilerplate code for standard CRUD operations.
 * Can be used across multiple apps.
 * 
 * @example
 * // Simple usage
 * const campaignTypesService = createCrudService<CampaignType>('/campaign-types');
 * 
 * @example
 * // With custom DTOs
 * const campaignOffersService = createCrudService<
 *   CampaignOffer,
 *   CreateOfferDto,
 *   UpdateOfferDto
 * >('/campaign-offers');
 * 
 * @example
 * // With toggle active support
 * const statusesService = createCrudService<Status>('/statuses', { hasToggleActive: true });
 */

import { apiClient } from '../../services/api';
import { CrudService } from '../hooks/useCrudOperations';

// =============================================================================
// TYPES
// =============================================================================

export interface CrudServiceOptions {
  /** Whether the service supports toggle active (default: false) */
  hasToggleActive?: boolean;
  /** Custom response data extractor */
  extractData?: <R>(response: unknown) => R;
}

// =============================================================================
// HELPER
// =============================================================================

/**
 * Default data extractor - handles common API response patterns
 */
const defaultExtractData = <R>(response: unknown): R => {
  const res = response as { data?: { data?: R } | R };
  // Handle nested data structure: { data: { data: [...] } }
  if (res.data && typeof res.data === 'object' && 'data' in res.data) {
    return res.data.data as R;
  }
  // Handle simple structure: { data: [...] }
  return (res.data ?? res) as R;
};

// =============================================================================
// FACTORY
// =============================================================================

/**
 * Creates a typed CRUD service for the given entity type and base URL.
 * 
 * @param baseUrl - API endpoint base URL (e.g., '/campaign-types')
 * @param options - Optional configuration
 * @returns A fully typed CRUD service
 */
export function createCrudService<
  T,
  CreateDto = Partial<T>,
  UpdateDto = Partial<T>
>(
  baseUrl: string,
  options: CrudServiceOptions = {}
): CrudService<T, CreateDto, UpdateDto> {
  const { hasToggleActive = false, extractData = defaultExtractData } = options;

  const service: CrudService<T, CreateDto, UpdateDto> = {
    /**
     * Get all entities
     */
    getAll: async (): Promise<T[]> => {
      const response = await apiClient.get(baseUrl);
      return extractData<T[]>(response) || [];
    },

    /**
     * Get a single entity by ID
     */
    getById: async (id: number): Promise<T> => {
      const response = await apiClient.get(`${baseUrl}/${id}`);
      return extractData<T>(response);
    },

    /**
     * Create a new entity
     */
    create: async (data: CreateDto): Promise<T> => {
      const response = await apiClient.post(baseUrl, data);
      return extractData<T>(response);
    },

    /**
     * Update an existing entity
     */
    update: async (id: number, data: UpdateDto): Promise<T> => {
      const response = await apiClient.put(`${baseUrl}/${id}`, data);
      return extractData<T>(response);
    },

    /**
     * Delete an entity
     */
    delete: async (id: number): Promise<void> => {
      await apiClient.delete(`${baseUrl}/${id}`);
    },
  };

  // Add toggleActive if supported
  if (hasToggleActive) {
    service.toggleActive = async (id: number): Promise<T> => {
      const response = await apiClient.patch(`${baseUrl}/${id}/toggle-active`);
      return extractData<T>(response);
    };
  }

  return service;
}

export default createCrudService;

